import {
  Activity,
  Antenna,
  Award,
  Briefcase,
  Building,
  ChartColumn,
  Cloud,
  Code,
  Cog,
  Cpu,
  Database,
  FileBadge,
  FileText,
  GitBranch,
  Headset,
  HeartPulse,
  Hospital,
  Landmark,
  Layers,
  Lightbulb,
  Megaphone,
  MessageSquare,
  Network,
  Plane,
  Quote,
  Rocket,
  Search,
  Shield,
  Sparkles,
  Star,
  Trophy,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { data } from "./data";
import { getRole, getTechnology } from "./queries";
import type { LensKey } from "../types";

// ── Hues (ported verbatim from the design prototype lib.jsx) ──────────────

/* Soft, pastel-leaning hue per industry — colorful without shouting. */
export const INDUSTRY_HUE: Record<string, string> = {
  healthcare: "#3fa89b",
  "financial-services": "#6f76e0",
  insurance: "#9b7fd4",
  government: "#7e93ab",
  utilities: "#46b387",
  travel: "#4f9fd6",
  telecom: "#c577cf",
  manufacturing: "#b3a07f",
  technology: "#dd9a4e",
};

export const TECH_CAT_HUE: Record<string, string> = {
  ccaas: "#7c3aed",
  ai: "#c026d3",
  crm: "#0284c7",
  marketing: "#e11d48",
  itsm: "#4f46e5",
  support: "#0d9488",
  telecom: "#c026d3",
  middleware: "#ea580c",
  eam: "#d97706",
  infrastructure: "#64748b",
  data: "#059669",
  integration: "#d97706",
  collaboration: "#475569",
  language: "#78716c",
};

/* Roles colour by seniority level — a meaningful gradient, not an arbitrary pick.
   Shared oklch L/C, hue walks executive → senior → mid (warm → green → blue). */
export const ROLE_LEVEL_HUE: Record<string, string> = {
  executive: "oklch(0.62 0.14 32)",
  senior: "oklch(0.62 0.13 158)",
  mid: "oklch(0.60 0.13 255)",
};

/* Skills have no inherent grouping, so an evenly-spaced hue wheel says "distinct
   peers". Same oklch L/C as the rest of the palette; only hue varies. */
export const SKILL_HUE: Record<string, string> = {};
data.skills.forEach((s, i) => {
  SKILL_HUE[s.id] = `oklch(0.64 0.13 ${Math.round((i * 360) / data.skills.length)})`;
});

// ── Icons (ported verbatim — kebab names mapped to lucide-react) ──────────

const INDUSTRY_ICON: Record<string, LucideIcon> = {
  healthcare: HeartPulse,
  "financial-services": Landmark,
  insurance: Shield,
  government: Building,
  utilities: Zap,
  travel: Plane,
  telecom: Antenna,
  manufacturing: Cog,
  technology: Cpu,
};

/* Per-skill iconography — appropriate to each capability. */
const SKILL_ICON: Record<string, LucideIcon> = {
  "cloud-transformation": Cloud,
  architecture: Network,
  "program-management": ChartColumn,
  "team-building": Award,
  "ai-driven-delivery": Sparkles,
  "tech-enablement": Code,
  "customer-engagement": Headset,
  "amazon-connect-sme": Antenna,
  "proposals-pricing": Quote,
  "vendor-management": GitBranch,
  "client-onboarding": Landmark,
  "marketing-automation": Megaphone,
};

/* Per-role iconography — the arc from analyst to chief architect. */
const ROLE_ICON: Record<string, LucideIcon> = {
  "chief-architect": Network,
  director: Briefcase,
  vp: Trophy,
  "program-manager": ChartColumn,
  "customer-success-pm": Headset,
  founder: Star,
  "managing-consultant": Quote,
  "team-lead": Layers,
  "senior-developer": Code,
  "senior-programmer": GitBranch,
  programmer: Code,
  "systems-analyst": Search,
};

const LENS_ICON: Record<string, LucideIcon> = {
  industry: Hospital,
  technology: Cpu,
  skill: Lightbulb,
  role: Trophy,
  timeline: Activity,
};

/* Group + per-category iconography for the Technology section */
const TECH_GROUP_ICON: Record<string, LucideIcon> = {
  ai: Sparkles,
  ccaas: Headset,
  crm: Briefcase,
  platforms: Layers,
  data: Database,
  infra: Cloud,
  lang: Code,
  collab: MessageSquare,
};

const TECH_CAT_ICON: Record<string, LucideIcon> = {
  ccaas: Headset,
  ai: Sparkles,
  crm: Briefcase,
  marketing: Megaphone,
  itsm: Cog,
  support: Headset,
  telecom: Antenna,
  middleware: Layers,
  eam: Cog,
  infrastructure: Cloud,
  data: Database,
  integration: GitBranch,
  collaboration: MessageSquare,
  language: Code,
};

export function industryIcon(id: string): LucideIcon {
  return INDUSTRY_ICON[id] ?? Briefcase;
}

export function skillIcon(id: string): LucideIcon {
  return SKILL_ICON[id] ?? Lightbulb;
}

export function roleIcon(id: string): LucideIcon {
  return ROLE_ICON[id] ?? Trophy;
}

export function techGroupIcon(key: string): LucideIcon {
  return TECH_GROUP_ICON[key] ?? Cpu;
}

export function techCatIcon(category?: string): LucideIcon {
  return TECH_CAT_ICON[category ?? ""] ?? Cpu;
}

export function technologyIcon(category?: string): LucideIcon {
  return techCatIcon(category);
}

export function lensIcon(lens: string): LucideIcon {
  return LENS_ICON[lens] ?? FileText;
}

/* One place every lens asks for an item's icon + hue. */
export function lensIconFor(lens: LensKey, id: string): LucideIcon {
  if (lens === "industry") return industryIcon(id);
  if (lens === "skill") return skillIcon(id);
  if (lens === "role") return roleIcon(id);
  return LENS_ICON[lens] ?? Activity;
}

export function lensHueFor(lens: LensKey, id: string): string {
  if (lens === "technology")
    return TECH_CAT_HUE[getTechnology(id)?.category ?? ""] ?? "var(--accent)";
  if (lens === "skill") return SKILL_HUE[id] ?? "var(--accent)";
  if (lens === "role") return ROLE_LEVEL_HUE[getRole(id)?.level ?? ""] ?? "var(--accent)";
  if (lens === "industry") return INDUSTRY_HUE[id] ?? "var(--accent)";
  return "var(--accent)";
}

// ── Technology domain grouping (ported verbatim from landing.jsx) ─────────

export interface TechGroupDef {
  key: string;
  name: string;
  cats: string[];
}

export const TECH_GROUPS: TechGroupDef[] = [
  { key: "ai", name: "AI & Machine Learning", cats: ["ai"] },
  { key: "ccaas", name: "Cloud Contact Center", cats: ["ccaas"] },
  { key: "crm", name: "CRM, Marketing & Service", cats: ["crm", "marketing", "support", "itsm"] },
  { key: "platforms", name: "Enterprise Platforms", cats: ["middleware", "eam", "telecom"] },
  { key: "data", name: "Data & Integration", cats: ["data", "integration"] },
  { key: "infra", name: "Infrastructure & Cloud", cats: ["infrastructure"] },
  { key: "lang", name: "Languages", cats: ["language"] },
  { key: "collab", name: "Collaboration", cats: ["collaboration"] },
];

export const qualificationIcon = FileBadge;
export const projectIcon = Rocket;

// ── Legacy Tailwind swatch system (pre-port components still import it) ───

export interface SwatchClasses {
  bg: string;
  bgSubtle: string;
  text: string;
  border: string;
  ring: string;
  dot: string;
}

// Explicit class strings so Tailwind v4's source scanning picks them up.
const SWATCHES: Record<string, SwatchClasses> = {
  teal: {
    bg: "bg-teal-100",
    bgSubtle: "bg-teal-50",
    text: "text-teal-900",
    border: "border-teal-200",
    ring: "ring-teal-300",
    dot: "bg-teal-500",
  },
  emerald: {
    bg: "bg-emerald-100",
    bgSubtle: "bg-emerald-50",
    text: "text-emerald-900",
    border: "border-emerald-200",
    ring: "ring-emerald-300",
    dot: "bg-emerald-500",
  },
  sky: {
    bg: "bg-sky-100",
    bgSubtle: "bg-sky-50",
    text: "text-sky-900",
    border: "border-sky-200",
    ring: "ring-sky-300",
    dot: "bg-sky-500",
  },
  indigo: {
    bg: "bg-indigo-100",
    bgSubtle: "bg-indigo-50",
    text: "text-indigo-900",
    border: "border-indigo-200",
    ring: "ring-indigo-300",
    dot: "bg-indigo-500",
  },
  violet: {
    bg: "bg-violet-100",
    bgSubtle: "bg-violet-50",
    text: "text-violet-900",
    border: "border-violet-200",
    ring: "ring-violet-300",
    dot: "bg-violet-500",
  },
  fuchsia: {
    bg: "bg-fuchsia-100",
    bgSubtle: "bg-fuchsia-50",
    text: "text-fuchsia-900",
    border: "border-fuchsia-200",
    ring: "ring-fuchsia-300",
    dot: "bg-fuchsia-500",
  },
  rose: {
    bg: "bg-rose-100",
    bgSubtle: "bg-rose-50",
    text: "text-rose-900",
    border: "border-rose-200",
    ring: "ring-rose-300",
    dot: "bg-rose-500",
  },
  amber: {
    bg: "bg-amber-100",
    bgSubtle: "bg-amber-50",
    text: "text-amber-900",
    border: "border-amber-200",
    ring: "ring-amber-300",
    dot: "bg-amber-500",
  },
  orange: {
    bg: "bg-orange-100",
    bgSubtle: "bg-orange-50",
    text: "text-orange-900",
    border: "border-orange-200",
    ring: "ring-orange-300",
    dot: "bg-orange-500",
  },
  slate: {
    bg: "bg-slate-100",
    bgSubtle: "bg-slate-50",
    text: "text-slate-900",
    border: "border-slate-200",
    ring: "ring-slate-300",
    dot: "bg-slate-500",
  },
  stone: {
    bg: "bg-stone-100",
    bgSubtle: "bg-stone-50",
    text: "text-stone-900",
    border: "border-stone-200",
    ring: "ring-stone-300",
    dot: "bg-stone-500",
  },
  zinc: {
    bg: "bg-zinc-100",
    bgSubtle: "bg-zinc-50",
    text: "text-zinc-900",
    border: "border-zinc-200",
    ring: "ring-zinc-300",
    dot: "bg-zinc-500",
  },
};

const INDUSTRY_SWATCH: Record<string, string> = {
  healthcare: "teal",
  "financial-services": "indigo",
  insurance: "violet",
  government: "slate",
  utilities: "emerald",
  travel: "sky",
  telecom: "fuchsia",
  manufacturing: "stone",
  technology: "amber",
  retail: "rose",
};

const TECHNOLOGY_CATEGORY_SWATCH: Record<string, string> = {
  ccaas: "violet",
  ai: "fuchsia",
  crm: "sky",
  marketing: "rose",
  itsm: "indigo",
  support: "teal",
  telecom: "fuchsia",
  middleware: "orange",
  eam: "amber",
  infrastructure: "zinc",
  data: "emerald",
  integration: "amber",
  collaboration: "slate",
  language: "stone",
};

export function industrySwatch(id: string): SwatchClasses {
  return SWATCHES[INDUSTRY_SWATCH[id] ?? "stone"];
}

export function technologySwatch(category?: string): SwatchClasses {
  return SWATCHES[TECHNOLOGY_CATEGORY_SWATCH[category ?? ""] ?? "stone"];
}

export function skillSwatch(): SwatchClasses {
  return SWATCHES.amber;
}

export function roleSwatch(): SwatchClasses {
  return SWATCHES.slate;
}

export function timelineSwatch(): SwatchClasses {
  return SWATCHES.emerald;
}

export function lensSwatch(lens: string, id: string, category?: string): SwatchClasses {
  if (lens === "industry") return industrySwatch(id);
  if (lens === "technology") return technologySwatch(category);
  if (lens === "skill") return skillSwatch();
  if (lens === "role") return roleSwatch();
  if (lens === "timeline") return timelineSwatch();
  return SWATCHES.stone;
}
